import { useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

export const EmailAuthForm = () => {
  const { passwordAuth } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const reg = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await passwordAuth(mode, reg ? form : { email: form.email, password: form.password });
      if (res === "check_email") toast.success("Check your inbox to confirm your email, then sign in.");
    } catch (err) {
      setError(err?.response ? errMsg(err) : err?.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3 text-left" data-testid="email-auth-form">
      <div className="grid grid-cols-2 gap-1 rounded-full bg-secondary/60 p-1">
        {[["login", "Sign in"], ["register", "Create account"]].map(([id, label]) => (
          <button key={id} type="button" data-testid={`email-auth-tab-${id}`} onClick={() => { setMode(id); setError(""); }}
            className={`h-9 rounded-full text-sm font-semibold transition-colors ${mode === id ? "bg-card shadow text-foreground" : "text-muted-foreground"}`}>{label}</button>
        ))}
      </div>
      {reg && <Input data-testid="register-name-input" value={form.name} onChange={set("name")} placeholder="Your name" maxLength={60} required autoComplete="name" className="h-12 rounded-xl" />}
      <Input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email" maxLength={254} required autoComplete="email" className="h-12 rounded-xl" />
      <div className="relative">
        <Input data-testid="auth-password-input" type={show ? "text" : "password"} value={form.password} onChange={set("password")} placeholder="Password" maxLength={128} required
          autoComplete={reg ? "new-password" : "current-password"} className="h-12 rounded-xl pr-11" />
        <button type="button" data-testid="toggle-password-visibility" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-3 top-3.5 text-muted-foreground">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {reg && <p className="text-[11px] text-muted-foreground">At least 8 characters with a letter and a number.</p>}
      {error && <div data-testid="email-auth-error" className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-sm">{error}</div>}
      <button type="submit" data-testid="email-auth-submit-button" disabled={busy}
        className="w-full h-12 rounded-full brand-gradient text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}{reg ? "Create account" : "Sign in with email"}
      </button>
      {!reg && <Link to="/forgot-password" data-testid="forgot-password-link" className="block text-center text-xs text-primary hover:underline">Forgot password?</Link>}
    </form>
  );
};
